import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@fluentui/react";

type QueryAccordionProps = {
  title?: string;
  value: string;
  defaultExpanded?: boolean;
  maxContentHeight?: number;
  className?: string;
};

export function QueryAccordion({
  title = "",
  value,
  defaultExpanded = true,
  maxContentHeight = 300,
  className,
}: QueryAccordionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hasText = useMemo(() => (value ?? "").trim().length > 0, [value]);

  return (
    <Root className={className}>
      <Header type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <Title>{title}</Title>

        <RightSide>
          <Chevron iconName="ChevronDown" $expanded={expanded} aria-hidden="true" />
        </RightSide>
      </Header>

      <ContentWrap $expanded={expanded} $maxH={maxContentHeight}>
        <ContentInner>{hasText ? <CodeBlock>{value}</CodeBlock> : <Empty>—</Empty>}</ContentInner>
      </ContentWrap>
    </Root>
  );
}

const Root = styled.div`
  border: 1px solid ${({ theme }) => theme.cayoTheme.brandColors.divider};
  border-radius: 4px;
  background: ${({ theme }) => theme.cayoTheme.brandColors.primaryBackground};
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  height: 34px;
  padding: 0 10px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border: none;
  cursor: pointer;

  background: ${({ theme }) => theme.cayoTheme.brandColors.secondaryBackground};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.cayoTheme.brandColors.primaryHighlight};
    outline-offset: -2px;
  }
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.cayoTheme.brandColors.deleted};
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Chevron = styled(Icon)<{ $expanded: boolean }>`
  font-size: 12px;
  color: ${({ theme }) => theme.cayoTheme.brandColors.disabled};
  transition: transform 160ms ease;
  transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
`;

const ContentWrap = styled.div<{ $expanded: boolean; $maxH: number }>`
  max-height: ${(p) => (p.$expanded ? `${p.$maxH}px` : "0px")};
  transition: max-height 200ms ease;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.cayoTheme.brandColors.divider};
`;

const ContentInner = styled.div`
  height: 100%;
  padding: 8px 10px;
  background: ${({ theme }) => theme.cayoTheme.brandColors.primaryBackground};
`;

const CodeBlock = styled.pre`
  margin: 0;
  height: 100%;
  max-height: 100%;
  overflow: auto;

  padding: 8px;

  border-radius: 4px;
  color: ${({ theme }) => theme.cayoTheme.brandColors.deleted};

  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  font-size: 12px;
  line-height: 1.35;

  white-space: pre-wrap;
  word-break: break-word;
`;

const Empty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.cayoTheme.brandColors.disabled};
`;
